import { useState, useEffect, useCallback } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  CreditCard, RefreshCw, Edit, Plus, Trash2, Check, X,
  DollarSign, Calendar, Users, Building2, Infinity, Crown,
  Settings, Gift, Mail, FileText, User, Save,
  AlertTriangle, Ban, Clock
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = ((typeof window !== 'undefined' && window.location.hostname.includes('seatpilot.co.uk')) ? 'https://seatpilot.co.uk' : (process.env.REACT_APP_BACKEND_URL || 'https://seatpilot.co.uk'));
const API = `${BACKEND_URL}/api`;

// Plan types with default pricing
const PLAN_TYPES = [
  { id: "free_unlimited", name: "Free Unlimited", description: "£0 with unlimited access", price: 0, overage: 0, seats: -1, layouts: -1, events: -1 },
  { id: "free_trial", name: "Free Trial", description: "30-day trial with limits", price: 0, overage: 0, seats: 500, layouts: 1, events: 1 },
  { id: "pay_as_you_go", name: "Pay As You Go", description: "£0.50 per seat", price: 0, overage: 0.50, seats: -1, layouts: -1, events: -1 },
  { id: "monthly", name: "Monthly", description: "£50/month with seat allowance", price: 50, overage: 0.30, seats: 10000, layouts: -1, events: -1 },
  { id: "enterprise", name: "Enterprise", description: "Custom pricing", price: 0, overage: 0, seats: -1, layouts: -1, events: -1 }
];

export default function BackofficeSubscriptions() {
  const { theme } = useOutletContext();
  const navigate = useNavigate();
  const isDark = theme === 'dark';

  const [plans, setPlans] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [standaloneUsers, setStandaloneUsers] = useState([]);
  const [whiteLabelSubs, setWhiteLabelSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("active");

  // PAYG Fees Settings
  const [paygFees, setPaygFees] = useState({
    setup_fee: 100,
    plan_switch_fee: 50,
    setup_fee_enabled: true,
    plan_switch_fee_enabled: true
  });
  const [savingPaygFees, setSavingPaygFees] = useState(false);

  // Retention Config
  const [retentionConfig, setRetentionConfig] = useState({
    discount_enabled: true,
    discount_percent: 20,
    free_month_enabled: true,
    free_months: 1
  });
  const [savingRetention, setSavingRetention] = useState(false);

  // Edit Subscription Dialog
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [subForm, setSubForm] = useState({
    plan_id: "free_unlimited",
    monthly_price: 0,
    monthly_seat_allowance: -1, // -1 = unlimited
    overage_per_seat: 0,
    max_events: -1, // -1 = unlimited
    max_layouts: -1, // -1 = unlimited
    billing_period: "monthly",
    expires_at: "",
    notes: ""
  });
  const [savingSub, setSavingSub] = useState(false);

  // Free Unlimited Confirmation
  const [showFreeConfirm, setShowFreeConfirm] = useState(false);
  const [freeConfirmCompany, setFreeConfirmCompany] = useState(null);

  // White Label Management
  const [updatingWhiteLabel, setUpdatingWhiteLabel] = useState(null);

  // Edit Plan Dialog
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planForm, setPlanForm] = useState({
    name: "",
    price_monthly: 0,
    price_yearly: 0,
    max_layouts: 10,
    max_events: 50,
    max_seats: 5000,
    features: [],
    is_active: true
  });

  // Terminate Plan Dialog (super admin only) — lets backoffice forcibly end
  // a customer's plan immediately or on a future date.
  const [showTerminateDialog, setShowTerminateDialog] = useState(false);
  const [terminateTarget, setTerminateTarget] = useState(null);
  const [terminateForm, setTerminateForm] = useState({
    mode: "immediate", // 'immediate' | 'scheduled'
    terminate_at: "",
    reason: "",
    notify_customer: true,
  });
  const [terminating, setTerminating] = useState(false);

  // Customer Details Popup — opens when the company name is clicked.
  // Shows the full customer profile (Customer ID, plan, email, phone, owner
  // info, subscription health) without leaving the Subscriptions table.
  const [customerDetails, setCustomerDetails] = useState(null);
  const [customerDetailsLoading, setCustomerDetailsLoading] = useState(false);

  const openCustomerDetails = async (company) => {
    setCustomerDetails({ company });
    setCustomerDetailsLoading(true);
    try {
      // Pull richer profile (owner phone, address etc.) plus the full
      // payment audit trail in parallel so the dialog renders with
      // everything in one paint.
      const [profileRes, auditRes] = await Promise.all([
        axios.get(`${API}/subscriptions/super-admin/companies/${company.company_id}`, { withCredentials: true }).catch(() => null),
        axios.get(`${API}/subscriptions/super-admin/companies/${company.company_id}/payment-audit?limit=100`, { withCredentials: true }).catch(() => null),
      ]);
      setCustomerDetails({
        company,
        profile: profileRes?.data || null,
        audit: auditRes?.data?.rows || [],
      });
    } finally {
      setCustomerDetailsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);
const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [plansRes, subsRes, companiesRes, whiteLabelRes, paygFeesRes, retentionRes] = await Promise.all([
        axios.get(`${API}/subscriptions/plans`, { withCredentials: true }).catch(() => ({ data: { plans: [] } })),
        axios.get(`${API}/super-admin/subscriptions`, { withCredentials: true }).catch(() => ({ data: { subscriptions: [] } })),
        axios.get(`${API}/subscriptions/admin/companies`, { withCredentials: true }).catch(() => ({ data: { companies: [] } })),
        axios.get(`${API}/subscriptions/admin/white-label-subscriptions`, { withCredentials: true }).catch(() => ({ data: { white_label_subscriptions: [] } })),
        axios.get(`${API}/platform/settings/payg-fees`, { withCredentials: true }).catch(() => ({ data: { payg_fees: {} } })),
        axios.get(`${API}/subscriptions/admin/retention-config`, { withCredentials: true }).catch(() => ({ data: {} }))
      ]);

      setPlans(plansRes.data.plans || []);
      setSubscriptions(subsRes.data.subscriptions || []);
      setCompanies(companiesRes.data.companies || []);
      setStandaloneUsers(companiesRes.data.standalone_users || []);
      setWhiteLabelSubs(whiteLabelRes.data.white_label_subscriptions || []);
      
      // Set PAYG fees
      const fees = paygFeesRes.data.payg_fees || {};
      setPaygFees({
        setup_fee: fees.setup_fee ?? 100,
        plan_switch_fee: fees.plan_switch_fee ?? 50,
        setup_fee_enabled: fees.setup_fee_enabled ?? true,
        plan_switch_fee_enabled: fees.plan_switch_fee_enabled ?? true
      });

      // Set retention config
      const rc = retentionRes.data || {};
      setRetentionConfig({
        discount_enabled: rc.discount_enabled ?? true,
        discount_percent: rc.discount_percent ?? 20,
        free_month_enabled: rc.free_month_enabled ?? true,
        free_months: rc.free_months ?? 1
      });
    } catch (error) {
      console.error("Failed to fetch subscriptions:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Save PAYG Fees
  const savePaygFees = async () => {
    setSavingPaygFees(true);
    try {
      await axios.put(`${API}/platform/settings/payg-fees`, paygFees, { withCredentials: true });
      toast.success("PAYG fees saved successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save PAYG fees");
    } finally {
      setSavingPaygFees(false);
    }
  };

  // Toggle white-label for a company
  const toggleWhiteLabel = async (companyId, currentStatus) => {
    setUpdatingWhiteLabel(companyId);
    try {
      await axios.put(`${API}/subscriptions/admin/white-label`, {
        company_id: companyId,
        white_label: !currentStatus,
        reason: "Admin manual update"
      }, { withCredentials: true });
      toast.success(`White-label ${!currentStatus ? 'enabled' : 'disabled'} successfully`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update white-label status");
    } finally {
      setUpdatingWhiteLabel(null);
    }
  };

  // Open edit subscription dialog
  const openEditSubscription = (company) => {
    const sub = company.subscription || {};
    setEditingSubscription(company);
    setSubForm({
      plan_id: sub.plan_id || "free_trial",
      monthly_price: sub.monthly_price || 0,
      monthly_seat_allowance: sub.monthly_seat_allowance ?? -1,
      overage_per_seat: sub.overage_per_seat || 0,
      max_events: sub.max_events ?? -1,
      max_layouts: sub.max_layouts ?? -1,
      billing_period: sub.billing_period || "monthly",
      expires_at: sub.expires_at || "",
      notes: sub.notes || "",
      apply_immediately: true,  // New: when to apply changes
      scheduled_date: ""  // New: specific date for scheduled changes
    });
    setShowEditDialog(true);
  };

  // Save subscription changes
  const saveSubscription = async () => {
    if (!editingSubscription) return;
    setSavingSub(true);
    try {
      await axios.post(`${API}/subscriptions/admin/configure-plan`, {
        company_id: editingSubscription.company_id,
        plan_id: subForm.plan_id,
        monthly_price: subForm.monthly_price,
        monthly_seat_allowance: subForm.monthly_seat_allowance,
        overage_per_seat: subForm.overage_per_seat,
        max_events: subForm.max_events === -1 ? null : subForm.max_events,
        max_layouts: subForm.max_layouts === -1 ? null : subForm.max_layouts,
        billing_period: subForm.billing_period,
        expires_at: subForm.expires_at || null,
        is_custom: subForm.plan_id === "free_unlimited" || subForm.plan_id === "enterprise",
        apply_immediately: subForm.apply_immediately,
        scheduled_date: subForm.scheduled_date || null
      }, { withCredentials: true });
      
      const message = subForm.apply_immediately 
        ? "Subscription updated - changes applied immediately"
        : subForm.scheduled_date 
          ? `Subscription updated - changes scheduled for ${formatDate(subForm.scheduled_date)}`
          : "Subscription updated - changes will apply at term end";
      
      toast.success(message);
      setShowEditDialog(false);
      setEditingSubscription(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update subscription");
    } finally {
      setSavingSub(false);
    }
  };

  // Quick actions - with confirmation
  const confirmFreeUnlimited = (company) => {
    setFreeConfirmCompany(company);
    setShowFreeConfirm(true);
  };

  const giveUnlimitedFree = async () => {
    if (!freeConfirmCompany) return;
    try {
      await axios.post(`${API}/subscriptions/admin/configure-plan`, {
        company_id: freeConfirmCompany.company_id,
        plan_id: "free_unlimited",
        monthly_price: 0,
        monthly_seat_allowance: -1,
        overage_per_seat: 0,
        max_events: null,
        max_layouts: null,
        is_custom: true
      }, { withCredentials: true });
      toast.success(`Granted free unlimited access to ${freeConfirmCompany.name}`);
      setShowFreeConfirm(false);
      setFreeConfirmCompany(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to update subscription");
    }
  };

  const savePlan = async () => {
    try {
      if (editingPlan) {
        await axios.put(`${API}/subscriptions/super-admin/plans/${editingPlan.plan_id}`, planForm, { withCredentials: true });
        toast.success("Plan updated");
      } else {
        await axios.post(`${API}/subscriptions/super-admin/plans`, planForm, { withCredentials: true });
        toast.success("Plan created");
      }
      setShowPlanDialog(false);
      setEditingPlan(null);
      resetPlanForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save plan");
    }
  };

  const deletePlan = async (planId) => {
    if (!window.confirm("Are you sure you want to delete this plan?")) return;
    try {
      const res = await axios.delete(`${API}/subscriptions/super-admin/plans/${planId}`, { withCredentials: true });
      toast.success(res.data?.message || "Plan deleted");
      // Force refresh plans list
      const plansRes = await axios.get(`${API}/subscriptions/plans`, { withCredentials: true });
      setPlans(plansRes.data.plans || {});
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete plan");
    }
  };

  // ---- Subscription lifecycle filters / actions ----
  // Each company has subscription.is_expired and subscription.expiring_soon
  // computed by the backend; we just split the list per tab here.
  const expiredCompanies = companies.filter(c => c?.subscription?.is_expired);
  const expiringSoonCompanies = companies.filter(
    c => c?.subscription?.expiring_soon && !c?.subscription?.is_expired
  );

  const openTerminateDialog = (company) => {
    setTerminateTarget(company);
    setTerminateForm({
      mode: "immediate",
      terminate_at: "",
      reason: "",
      notify_customer: true,
    });
    setShowTerminateDialog(true);
  };

  const submitTerminate = async () => {
    if (!terminateTarget) return;
    if (terminateForm.mode === "scheduled" && !terminateForm.terminate_at) {
      toast.error("Please pick a termination date");
      return;
    }
    setTerminating(true);
    try {
      // ISO-encode the date input (HTML date input gives "YYYY-MM-DD")
      const payload = {
        mode: terminateForm.mode,
        terminate_at: terminateForm.terminate_at
          ? new Date(terminateForm.terminate_at + "T23:59:59Z").toISOString()
          : null,
        reason: terminateForm.reason,
        notify_customer: terminateForm.notify_customer,
      };
      await axios.post(
        `${API}/subscriptions/super-admin/companies/${terminateTarget.company_id}/terminate-plan`,
        payload,
        { withCredentials: true }
      );
      toast.success(
        terminateForm.mode === "immediate"
          ? "Subscription terminated"
          : `Termination scheduled for ${terminateForm.terminate_at}`
      );
      setShowTerminateDialog(false);
      setTerminateTarget(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to terminate plan");
    } finally {
      setTerminating(false);
    }
  };

  const openEditPlan = (planId, plan) => {
    setEditingPlan({ ...plan, plan_id: planId });
    
    // Properly extract values from plan
    const isYearly = plan.billing_period === 'yearly';
    const monthlyPrice = isYearly ? (plan.price / 12) : (plan.price || 0);
    const yearlyPrice = isYearly ? plan.price : (plan.price ? plan.price * 12 * 0.8 : 0); // 20% discount
    
    setPlanForm({
      name: plan.name || "",
      price_monthly: monthlyPrice,
      price_yearly: yearlyPrice,
      per_seat_price: plan.per_seat_price || 0,
      overage_per_seat: plan.overage_per_seat || 0,
      monthly_seat_allowance: plan.monthly_seat_allowance || 0,
      // null or undefined means unlimited, convert to special value
      max_layouts: plan.max_layouts === null || plan.max_layouts === undefined ? -1 : plan.max_layouts,
      max_events: plan.max_events === null || plan.max_events === undefined ? -1 : plan.max_events,
      max_seats: plan.max_seats || 0,
      api_key_limit: plan.api_key_limit || 3,
      features: plan.features || [],
      // Pre-populate with auto-generated features if no custom ones set
      homepage_features: (plan.homepage_features?.length > 0) ? plan.homepage_features : (plan.auto_homepage_features || []),
      is_active: plan.is_active !== false,
      show_on_homepage: plan.show_on_homepage === true,
      is_popular: plan.is_popular === true,
      description: plan.description || "",
      billing_period: plan.billing_period || "monthly",
      discount_percent: plan.discount_percent || 0
    });
    setShowPlanDialog(true);
  };

  const resetPlanForm = () => {
    setPlanForm({
      name: "",
      price_monthly: 0,
      price_yearly: 0,
      per_seat_price: 0,
      overage_per_seat: 0,
      monthly_seat_allowance: 0,
      max_layouts: -1,
      max_events: -1,
      max_seats: 5000,
      api_key_limit: 3,
      features: [],
      homepage_features: [],
      is_active: true,
      show_on_homepage: true,
      is_popular: false,
      description: ""
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  const getPlanBadge = (planId, isCustom) => {
    if (isCustom || planId === "free_unlimited") {
      return <span className="px-2 py-1 rounded text-xs font-medium bg-violet-500/20 text-violet-400 flex items-center gap-1"><Crown className="w-3 h-3" /> Custom</span>;
    }
    const badges = {
      free_trial: "bg-blue-500/20 text-blue-400",
      pay_as_you_go: "bg-green-500/20 text-green-400",
      monthly: "bg-amber-500/20 text-amber-400",
      enterprise: "bg-purple-500/20 text-purple-400"
    };
    return <span className={`px-2 py-1 rounded text-xs font-medium ${badges[planId] || 'bg-slate-500/20 text-slate-400'}`}>{planId?.replace(/_/g, ' ')}</span>;
  };

  // Helper function to check if plan should have yearly option
  const isYearlyApplicable = (planName) => {
    const name = (planName || '').toLowerCase();
    // Yearly doesn't apply to free, trial, or PAYG plans
    return !name.includes('free') && !name.includes('trial') && !name.includes('pay as you go') && !name.includes('payg');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Subscriptions</h1>
          <p className={`mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Manage customer subscriptions and plans</p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm" className="gap-2">
          <RefreshCw className="w-4 h-4" /> Refresh
        </Button>
      </div>

      {/* Tab Navigation */}
      <div className={`flex gap-2 border-b overflow-x-auto ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
        {[
          { id: "active", label: `Customer Subscriptions (${companies.length})`, icon: Users },
          { id: "expiring", label: `Expiring Soon (${expiringSoonCompanies.length})`, icon: Clock, accent: 'amber' },
          { id: "expired", label: `Expired (${expiredCompanies.length})`, icon: AlertTriangle, accent: 'red' },
          { id: "whitelabel", label: `White-Label (${whiteLabelSubs.length})`, icon: Crown },
          { id: "plans", label: "Subscription Plans", icon: CreditCard },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`subs-tab-${tab.id}`}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? (tab.accent === 'red'
                    ? 'border-red-600 text-red-600'
                    : tab.accent === 'amber'
                      ? 'border-amber-600 text-amber-600'
                      : 'border-violet-600 text-violet-600')
                : `border-transparent ${isDark ? 'text-slate-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-violet-500" />
        </div>
      ) : (
        <>
          {/* Customer Subscriptions Tab + the new Expired/Expiring Soon tabs
              all share the same renderer to avoid drift. The list is filtered
              on the client side based on the currently selected tab. */}
          {(activeTab === "active" || activeTab === "expired" || activeTab === "expiring") && (
            <Card className={`p-6 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
              {(() => {
                const visible = activeTab === "expired"
                  ? expiredCompanies
                  : activeTab === "expiring"
                    ? expiringSoonCompanies
                    : companies;
                const heading = activeTab === "expired"
                  ? `Expired Subscriptions (${visible.length})`
                  : activeTab === "expiring"
                    ? `Expiring in next 30 days (${visible.length})`
                    : `Customer Subscriptions (${visible.length})`;
                const empty = activeTab === "expired"
                  ? "No expired subscriptions"
                  : activeTab === "expiring"
                    ? "No subscriptions expiring soon"
                    : "No customers found";

                return (
                  <>
                    <div className="flex items-center justify-between mb-6">
                      <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{heading}</h3>
                      {activeTab === "expiring" && visible.length > 0 && (
                        <span className="text-xs text-amber-600">Auto-renew is OFF for these accounts</span>
                      )}
                    </div>
                    {visible.length === 0 ? (
                      <div className="text-center py-12">
                        <Users className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-slate-600' : 'text-gray-300'}`} />
                        <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>{empty}</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className={`border-b ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
                              <th className={`text-left py-3 px-4 text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Company</th>
                              <th className={`text-left py-3 px-4 text-sm font-medium hidden md:table-cell ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Customer ID</th>
                              <th className={`text-left py-3 px-4 text-sm font-medium hidden lg:table-cell ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Email</th>
                              <th className={`text-left py-3 px-4 text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Plan</th>
                              <th className={`text-left py-3 px-4 text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Price</th>
                              <th className={`text-left py-3 px-4 text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Renewal/Expiry</th>
                              <th className={`text-left py-3 px-4 text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Status</th>
                              <th className={`text-left py-3 px-4 text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {visible.map(company => {
                              const sub = company.subscription || {};
                              const isExpired = sub.is_expired;
                              const expiringSoon = sub.expiring_soon;
                              // Visually flag expired rows red, expiring-soon rows amber.
                              const rowCls = isExpired
                                ? (isDark ? 'border-red-900/40 bg-red-900/10 hover:bg-red-900/20' : 'border-red-200 bg-red-50 hover:bg-red-100')
                                : expiringSoon
                                  ? (isDark ? 'border-amber-900/40 bg-amber-900/10 hover:bg-amber-900/20' : 'border-amber-200 bg-amber-50 hover:bg-amber-100')
                                  : (isDark ? 'border-slate-700 hover:bg-slate-700/50' : 'border-gray-100 hover:bg-gray-50');
                              return (
                                <tr key={company.company_id} className={`border-b ${rowCls}`} data-testid={`subs-row-${company.company_id}`}>
                                  <td className={`py-3 px-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                    <div className="flex items-center gap-2">
                                      <Building2 className={`w-4 h-4 ${isDark ? 'text-slate-400' : 'text-gray-400'}`} />
                                      <div>
                                        <p className="font-medium flex items-center gap-2">
                                          <button
                                            type="button"
                                            onClick={() => openCustomerDetails(company)}
                                            className="text-left hover:text-violet-600 hover:underline cursor-pointer"
                                            data-testid={`open-customer-${company.company_id}`}
                                          >
                                            {company.name || 'Unnamed'}
                                          </button>
                                          {isExpired && (
                                            <span
                                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-700 border border-red-200"
                                              data-testid="expired-flag"
                                            >
                                              <AlertTriangle className="w-2.5 h-2.5" /> Expired
                                            </span>
                                          )}
                                          {!isExpired && expiringSoon && (
                                            <span
                                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700 border border-amber-200"
                                              data-testid="expiring-soon-flag"
                                            >
                                              <Clock className="w-2.5 h-2.5" /> {sub.days_until_expiry}d
                                            </span>
                                          )}
                                        </p>
                                        <p className={`text-xs md:hidden ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>{company.owner_email}</p>
                                      </div>
                                    </div>
                                  </td>
                                  <td className={`py-3 px-4 hidden md:table-cell ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                                    <code className={`text-xs font-mono px-2 py-0.5 rounded ${isDark ? 'bg-violet-500/20 text-violet-400' : 'bg-violet-100 text-violet-700'}`}>
                                      {company.customer_id || `SP-${company.company_id?.slice(-6).toUpperCase()}`}
                                    </code>
                                  </td>
                                  <td className={`py-3 px-4 hidden lg:table-cell text-sm ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                                    {company.owner_email || '—'}
                                  </td>
                                  <td className="py-3 px-4">{getPlanBadge(sub.plan_id, sub.is_custom)}</td>
                                  <td className={`py-3 px-4 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                                    <div>
                                      <p className="font-medium">{formatCurrency(sub.monthly_price || 0)}/mo</p>
                                      {sub.overage_per_seat > 0 && (
                                        <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>+{formatCurrency(sub.overage_per_seat)}/seat overage</p>
                                      )}
                                    </div>
                                  </td>
                                  <td className={`py-3 px-4 text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                                    {sub.expires_at ? (
                                      <div>
                                        <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                                          {['monthly', 'enterprise'].includes(sub.plan_id) && sub.status === 'active' ? 'Renews on' : 'Expires on'}
                                        </p>
                                        <p className={`font-medium ${isExpired ? 'text-red-600' : (expiringSoon ? 'text-amber-600' : '')}`}>{formatDate(sub.expires_at)}</p>
                                        {sub.scheduled_termination_at && (
                                          <div className="mt-1 text-xs text-red-500">
                                            <p data-testid="sub-termination-scheduled">⚠ Terminates on {formatDate(sub.scheduled_termination_at)}</p>
                                            {sub.termination_reason && (
                                              <p className="text-[11px] italic text-red-400 mt-0.5" data-testid="sub-termination-reason">
                                                Reason: {sub.termination_reason}
                                              </p>
                                            )}
                                            {sub.terminated_by_email && (
                                              <p className="text-[11px] text-gray-400 mt-0.5">By {sub.terminated_by_email}</p>
                                            )}
                                          </div>
                                        )}
                                        {sub.pending_downgrade && (
                                          <p className="text-xs text-amber-500 mt-1">⚠ Downgrade to {sub.pending_downgrade} pending</p>
                                        )}
                                      </div>
                                    ) : sub.plan_id === 'free_unlimited' || sub.plan_id === 'pay_as_you_go' ? (
                                      <span className="text-green-600 text-xs">No expiry</span>
                                    ) : (
                                      <span className={isDark ? 'text-slate-500' : 'text-gray-400'}>-</span>
                                    )}
                                  </td>
                                  <td className="py-3 px-4">
                                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                                      sub.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                      sub.status === 'trial' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                                      (sub.status === 'cancelled' || sub.status === 'expired') ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                                      'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-400'
                                    }`}>
                                      {sub.status || 'none'}
                                    </span>
                                  </td>
                                  <td className="py-3 px-4">
                                    <div className="flex gap-2">
                                      <Button variant="outline" size="sm" onClick={() => openEditSubscription(company)}>
                                        <Edit className="w-3 h-3 mr-1" /> Edit
                                      </Button>
                                      <Button variant="ghost" size="sm" className="text-violet-500" onClick={() => confirmFreeUnlimited(company)} title="Give Free Unlimited">
                                        <Gift className="w-3 h-3" />
                                      </Button>
                                      {!isExpired && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                                          onClick={() => openTerminateDialog(company)}
                                          data-testid={`terminate-btn-${company.company_id}`}
                                          title="Terminate plan"
                                        >
                                          <Ban className="w-3 h-3" />
                                        </Button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                );
              })()}
            </Card>
          )}

          {/* White-Label Subscriptions Tab */}
          {activeTab === "whitelabel" && (
            <Card className={`p-6 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>White-Label Subscriptions</h3>
                  <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Customers who have removed SeatPilot branding (£20/month)</p>
                </div>
              </div>

              {whiteLabelSubs.length === 0 ? (
                <div className="text-center py-12">
                  <Crown className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-slate-600' : 'text-gray-300'}`} />
                  <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>No white-label subscriptions</p>
                  <p className={`text-sm mt-1 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>Customers can subscribe from their dashboard</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className={`border-b ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
                        <th className={`text-left py-3 px-4 text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Company</th>
                        <th className={`text-left py-3 px-4 text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Email</th>
                        <th className={`text-left py-3 px-4 text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Subscribed</th>
                        <th className={`text-left py-3 px-4 text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Status</th>
                        <th className={`text-left py-3 px-4 text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {whiteLabelSubs.map(wl => (
                        <tr key={wl.company_id} className={`border-b ${isDark ? 'border-slate-700 hover:bg-slate-700/50' : 'border-gray-100 hover:bg-gray-50'}`}>
                          <td className={`py-3 px-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            <div className="flex items-center gap-2">
                              <Crown className={`w-4 h-4 ${wl.white_label ? 'text-amber-500' : 'text-gray-400'}`} />
                              <span className="font-medium">{wl.company_name}</span>
                            </div>
                          </td>
                          <td className={`py-3 px-4 ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                            {wl.email || wl.owner_email || wl.admin_email || '—'}
                          </td>
                          <td className={`py-3 px-4 text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                            {wl.white_label_subscribed_at ? formatDate(wl.white_label_subscribed_at) : '-'}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              wl.white_label 
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                                : 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-400'
                            }`}>
                              {wl.white_label ? 'Active' : 'Disabled'}
                            </span>
                            {wl.white_label_status === 'admin_granted' && (
                              <span className="ml-1 px-1.5 py-0.5 bg-violet-100 text-violet-700 text-[10px] rounded">Admin</span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={wl.white_label}
                                onCheckedChange={() => toggleWhiteLabel(wl.company_id, wl.white_label)}
                                disabled={updatingWhiteLabel === wl.company_id}
                              />
                              <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                                {wl.white_label ? 'Enabled' : 'Disabled'}
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          )}

          {/* Subscription Plans Tab */}
          {activeTab === "plans" && (
            <>
            <Card className={`p-6 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
              <div className="flex items-center justify-between mb-6">
                <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Subscription Plans</h3>
                <Button size="sm" onClick={() => {
                  setEditingPlan(null);
                  resetPlanForm();
                  setShowPlanDialog(true);
                }}>
                  <Plus className="w-4 h-4 mr-1" /> Add Plan
                </Button>
              </div>

              {Object.keys(plans).length === 0 ? (
                <div className="text-center py-12">
                  <CreditCard className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-slate-600' : 'text-gray-300'}`} />
                  <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>No subscription plans configured</p>
                </div>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {Object.entries(plans).map(([planId, plan]) => (
                    <div key={planId} className={`p-5 rounded-lg border ${plan.is_active === false ? 'opacity-60' : ''} ${isDark ? 'bg-slate-700/50 border-slate-600' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{plan.name}</h4>
                            {plan.is_active === false ? (
                              <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-[10px] rounded font-medium">Inactive</span>
                            ) : (
                              <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] rounded font-medium">Active</span>
                            )}
                            {plan.show_on_homepage && (
                              <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-[10px] rounded">Public</span>
                            )}
                            {plan.is_popular && (
                              <span className="px-1.5 py-0.5 bg-violet-100 text-violet-700 text-[10px] rounded font-medium">Most Popular</span>
                            )}
                          </div>
                          <p className={`text-2xl font-bold mt-1 ${isDark ? 'text-violet-400' : 'text-violet-600'}`}>
                            {plan.price === null ? 'Custom' : formatCurrency(plan.price)}
                            {plan.price !== null && <span className={`text-sm font-normal ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{plan.billing_period === 'yearly' ? '/yr' : '/mo'}</span>}
                          </p>
                        </div>
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => openEditPlan(planId, plan)}
                          >
                            <Edit className="w-3 h-3" />
                          </Button>
                          {!plan.is_default && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-500 hover:text-red-700"
                              onClick={() => deletePlan(planId)}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                      <p className={`text-sm mb-3 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{plan.description}</p>
                      <div className={`text-xs space-y-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                        {plan.per_seat_price > 0 && (
                          <p>Per seat: {formatCurrency(plan.per_seat_price)}</p>
                        )}
                        {plan.overage_per_seat > 0 && (
                          <p>Overage: {formatCurrency(plan.overage_per_seat)}/seat after allowance</p>
                        )}
                        {plan.monthly_seat_allowance > 0 && (
                          <p>Seat allowance: {plan.monthly_seat_allowance.toLocaleString()}/mo</p>
                        )}

                        {/* Homepage Features */}
                        {(plan.homepage_features?.length > 0 || plan.auto_homepage_features?.length > 0 || plan.features?.length > 0) && (
                          <div className={`mt-2 pt-2 border-t ${isDark ? 'border-slate-600' : 'border-gray-200'}`}>
                            <p className={`text-[10px] font-semibold mb-1 ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                              {plan.homepage_features?.length > 0 ? 'Homepage Features:' : 'Auto-generated Features:'}
                            </p>
                            <div className="space-y-0.5">
                              {(plan.homepage_features?.length > 0 ? plan.homepage_features : (plan.auto_homepage_features || plan.features || [])).map((feat, idx) => (
                                <div key={idx} className="flex items-center justify-between group">
                                  <span className="text-[11px] flex items-center gap-1">
                                    <span className="text-green-500">&#10003;</span> {feat}
                                  </span>
                                  {plan.homepage_features?.length > 0 && (
                                    <button
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        const currentFeatures = [...(plan.homepage_features || [])];
                                        currentFeatures.splice(idx, 1);
                                        try {
                                          const updatedPlan = { ...plan, homepage_features: currentFeatures, price_monthly: plan.price || 0 };
                                          await axios.put(`${API}/subscriptions/super-admin/plans/${planId}`, updatedPlan, { withCredentials: true });
                                          fetchData();
                                          toast.success(`Feature removed`);
                                        } catch (err) { toast.error('Failed to remove feature'); }
                                      }}
                                      className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 p-0.5 transition-opacity"
                                      title="Remove feature"
                                    >
                                      <Trash2 className="w-2.5 h-2.5" />
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                            {!plan.homepage_features?.length && (
                              <p className={`text-[9px] mt-1 italic ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                                Auto-generated from plan settings. Edit plan to customise.
                              </p>
                            )}
                          </div>
                        )}

                        <div className="flex gap-2 mt-2 pt-2 border-t border-gray-200">
                          <span className={`px-1.5 py-0.5 rounded ${plan.max_layouts === null || plan.max_layouts === undefined ? 'bg-green-100 text-green-700' : plan.max_layouts === -1 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                            {plan.max_layouts === null || plan.max_layouts === undefined || plan.max_layouts === -1 ? '∞ Layouts' : `${plan.max_layouts} Layouts`}
                          </span>
                          <span className={`px-1.5 py-0.5 rounded ${plan.max_events === null || plan.max_events === undefined ? 'bg-green-100 text-green-700' : plan.max_events === -1 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                            {plan.max_events === null || plan.max_events === undefined || plan.max_events === -1 ? '∞ Events' : `${plan.max_events} Events`}
                          </span>
                          <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">
                            {plan.api_key_limit || 3} API Keys
                          </span>
                        </div>
                      </div>

                      {/* Quick Toggle Switches */}
                      <div className={`mt-3 pt-3 border-t ${isDark ? 'border-slate-600' : 'border-gray-200'} space-y-2`}>
                        <div className="flex items-center justify-between">
                          <label className={`text-xs font-medium ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>Active</label>
                          <Switch
                            checked={plan.is_active !== false}
                            data-testid={`toggle-active-${planId}`}
                            onCheckedChange={async (checked) => {
                              try {
                                await axios.patch(`${API}/subscriptions/super-admin/plans/${planId}/toggle`, { field: "is_active", value: checked }, { withCredentials: true });
                                fetchData();
                                toast.success(`${plan.name} ${checked ? 'activated' : 'deactivated'}`);
                              } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
                            }}
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <label className={`text-xs font-medium ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>Show on Homepage</label>
                          <Switch
                            checked={plan.show_on_homepage === true}
                            data-testid={`toggle-homepage-${planId}`}
                            onCheckedChange={async (checked) => {
                              try {
                                await axios.patch(`${API}/subscriptions/super-admin/plans/${planId}/toggle`, { field: "show_on_homepage", value: checked }, { withCredentials: true });
                                fetchData();
                                toast.success(`${plan.name} ${checked ? 'shown on' : 'hidden from'} homepage`);
                              } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
                            }}
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <label className={`text-xs font-medium ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>Most Popular</label>
                          <Switch
                            checked={plan.is_popular === true}
                            data-testid={`toggle-popular-${planId}`}
                            onCheckedChange={async (checked) => {
                              try {
                                await axios.patch(`${API}/subscriptions/super-admin/plans/${planId}/toggle`, { field: "is_popular", value: checked }, { withCredentials: true });
                                fetchData();
                                toast.success(checked ? `${plan.name} marked as Most Popular` : `${plan.name} removed from Most Popular`);
                              } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* PAYG Setup Fees Settings */}
            <Card className={`p-6 mt-4 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Pay As You Go (PAYG) Setup Fees</h3>
                  <p className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                    Configure one-time setup fees for PAYG subscriptions
                  </p>
                </div>
              </div>
              
              <div className="grid gap-4 md:grid-cols-2">
                {/* First-Time Setup Fee */}
                <div className={`p-4 rounded-lg border ${isDark ? 'bg-slate-700/50 border-slate-600' : 'bg-gray-50 border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>First-Time Setup Fee</h4>
                      <p className={`text-xs mt-0.5 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                        Charged once when a new customer subscribes to PAYG
                      </p>
                    </div>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={paygFees.setup_fee_enabled}
                        onChange={(e) => setPaygFees({...paygFees, setup_fee_enabled: e.target.checked})}
                        className="rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                      />
                      <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Enabled</span>
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-lg ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>£</span>
                    <Input
                      type="number"
                      value={paygFees.setup_fee}
                      onChange={(e) => setPaygFees({...paygFees, setup_fee: parseFloat(e.target.value) || 0})}
                      min="0"
                      step="10"
                      disabled={!paygFees.setup_fee_enabled}
                      className={`w-24 ${!paygFees.setup_fee_enabled ? 'opacity-50' : ''}`}
                    />
                  </div>
                </div>

                {/* Re-Subscribe Fee (50% Discount) */}
                <div className={`p-4 rounded-lg border ${isDark ? 'bg-slate-700/50 border-slate-600' : 'bg-gray-50 border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Re-Subscribe Fee (50% Off)</h4>
                      <p className={`text-xs mt-0.5 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                        Charged when returning to PAYG after cancellation
                      </p>
                    </div>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={paygFees.plan_switch_fee_enabled}
                        onChange={(e) => setPaygFees({...paygFees, plan_switch_fee_enabled: e.target.checked})}
                        className="rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                      />
                      <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Enabled</span>
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-lg ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>£</span>
                    <Input
                      type="number"
                      value={paygFees.plan_switch_fee}
                      onChange={(e) => setPaygFees({...paygFees, plan_switch_fee: parseFloat(e.target.value) || 0})}
                      min="0"
                      step="10"
                      disabled={!paygFees.plan_switch_fee_enabled}
                      className={`w-24 ${!paygFees.plan_switch_fee_enabled ? 'opacity-50' : ''}`}
                    />
                    <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-400'}`}>
                      ({Math.round((paygFees.plan_switch_fee / paygFees.setup_fee) * 100) || 50}% of setup fee)
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <Button 
                  onClick={savePaygFees}
                  disabled={savingPaygFees}
                  className="gap-2"
                >
                  {savingPaygFees ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {savingPaygFees ? 'Saving...' : 'Save PAYG Settings'}
                </Button>
              </div>
            </Card>

            {/* Cancellation Retention Settings */}
            <Card className={`p-6 mt-6 ${isDark ? 'bg-slate-800/50 border-slate-700' : ''}`}>
              <h3 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Cancellation Retention Offers</h3>
              <p className={`text-sm mb-4 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                Configure the offers shown to customers when they try to cancel their subscription.
              </p>
              
              <div className="space-y-4">
                {/* Discount Offer */}
                <div className={`p-4 rounded-lg border ${isDark ? 'bg-slate-700/50 border-slate-600' : 'bg-gray-50 border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Step 1: Discount Offer</p>
                      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Offer a discount to retain the customer</p>
                    </div>
                    <Switch
                      checked={retentionConfig.discount_enabled}
                      onCheckedChange={(checked) => setRetentionConfig({...retentionConfig, discount_enabled: checked})}
                    />
                  </div>
                  {retentionConfig.discount_enabled && (
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={retentionConfig.discount_percent}
                        onChange={(e) => setRetentionConfig({...retentionConfig, discount_percent: parseFloat(e.target.value) || 0})}
                        min="1"
                        max="100"
                        className="w-20"
                      />
                      <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>% discount</span>
                    </div>
                  )}
                </div>

                {/* Free Month Offer */}
                <div className={`p-4 rounded-lg border ${isDark ? 'bg-slate-700/50 border-slate-600' : 'bg-gray-50 border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Step 2: Free Month Offer</p>
                      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Offer free month(s) as a last retention attempt</p>
                    </div>
                    <Switch
                      checked={retentionConfig.free_month_enabled}
                      onCheckedChange={(checked) => setRetentionConfig({...retentionConfig, free_month_enabled: checked})}
                    />
                  </div>
                  {retentionConfig.free_month_enabled && (
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={retentionConfig.free_months}
                        onChange={(e) => setRetentionConfig({...retentionConfig, free_months: parseInt(e.target.value) || 1})}
                        min="1"
                        max="12"
                        className="w-20"
                      />
                      <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>free month(s)</span>
                    </div>
                  )}
                </div>

                <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                  Both offers are one-time only per customer. Once used, they won&apos;t see that offer again.
                </p>
              </div>

              <div className="mt-4 flex justify-end">
                <Button 
                  onClick={async () => {
                    setSavingRetention(true);
                    try {
                      await axios.put(`${API}/subscriptions/admin/retention-config`, retentionConfig, { withCredentials: true });
                      toast.success("Retention settings saved");
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Failed to save");
                    } finally { setSavingRetention(false); }
                  }}
                  disabled={savingRetention}
                  className="gap-2"
                >
                  {savingRetention ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {savingRetention ? 'Saving...' : 'Save Retention Settings'}
                </Button>
              </div>
            </Card>
            </>
          )}
        </>
      )}

      {/* Edit Subscription Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>Edit Subscription - {editingSubscription?.name}</DialogTitle>
            <DialogDescription>Configure subscription for this company</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto py-4">
            <div>
              <Label>Plan Type</Label>
              <Select value={subForm.plan_id} onValueChange={(v) => {
                // Find the selected plan's default values
                const selectedPlan = PLAN_TYPES.find(p => p.id === v);
                if (selectedPlan) {
                  setSubForm({
                    ...subForm,
                    plan_id: v,
                    monthly_price: selectedPlan.price,
                    overage_per_seat: selectedPlan.overage,
                    monthly_seat_allowance: selectedPlan.seats,
                    max_layouts: selectedPlan.layouts,
                    max_events: selectedPlan.events
                  });
                }
              }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PLAN_TYPES.map(plan => (
                    <SelectItem key={plan.id} value={plan.id}>
                      <div className="flex flex-col">
                        <span>{plan.name}</span>
                        <span className="text-xs text-slate-500">{plan.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Monthly Price (£)</Label>
                <Input 
                  type="number" 
                  min="0"
                  value={subForm.monthly_price} 
                  onChange={(e) => setSubForm({...subForm, monthly_price: parseFloat(e.target.value) || 0})} 
                />
              </div>
              <div>
                <Label>Overage Per Seat (£)</Label>
                <Input 
                  type="number" 
                  min="0"
                  step="0.01"
                  value={subForm.overage_per_seat} 
                  onChange={(e) => setSubForm({...subForm, overage_per_seat: parseFloat(e.target.value) || 0})} 
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Monthly Seat Allowance</Label>
                <div className="flex items-center gap-2">
                  <Switch 
                    checked={subForm.monthly_seat_allowance === -1}
                    onCheckedChange={(checked) => setSubForm({...subForm, monthly_seat_allowance: checked ? -1 : 1000})}
                  />
                  <span className="text-sm text-slate-500">Unlimited</span>
                </div>
              </div>
              {subForm.monthly_seat_allowance !== -1 && (
                <Input 
                  type="number" 
                  min="1"
                  value={subForm.monthly_seat_allowance} 
                  onChange={(e) => setSubForm({...subForm, monthly_seat_allowance: parseInt(e.target.value) || 1000})} 
                />
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Max Layouts</Label>
                  <div className="flex items-center gap-2">
                    <Switch 
                      checked={subForm.max_layouts === -1}
                      onCheckedChange={(checked) => setSubForm({...subForm, max_layouts: checked ? -1 : 10})}
                    />
                    <span className="text-xs text-slate-500">∞</span>
                  </div>
                </div>
                {subForm.max_layouts !== -1 && (
                  <Input 
                    type="number" 
                    min="1"
                    value={subForm.max_layouts} 
                    onChange={(e) => setSubForm({...subForm, max_layouts: parseInt(e.target.value) || 10})} 
                  />
                )}
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Max Events</Label>
                  <div className="flex items-center gap-2">
                    <Switch 
                      checked={subForm.max_events === -1}
                      onCheckedChange={(checked) => setSubForm({...subForm, max_events: checked ? -1 : 50})}
                    />
                    <span className="text-xs text-slate-500">∞</span>
                  </div>
                </div>
                {subForm.max_events !== -1 && (
                  <Input 
                    type="number" 
                    min="1"
                    value={subForm.max_events} 
                    onChange={(e) => setSubForm({...subForm, max_events: parseInt(e.target.value) || 50})} 
                  />
                )}
              </div>
            </div>

            <div>
              <Label>Notes (Internal)</Label>
              <Textarea 
                value={subForm.notes}
                onChange={(e) => setSubForm({...subForm, notes: e.target.value})}
                placeholder="Internal notes about this subscription..."
                rows={2}
              />
            </div>

            {/* Billing Period */}
            <div>
              <Label>Billing Period (Rolling)</Label>
              <Select value={subForm.billing_period} onValueChange={(v) => setSubForm({...subForm, billing_period: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly (Rolling)</SelectItem>
                  <SelectItem value="yearly">Yearly (Rolling)</SelectItem>
                  <SelectItem value="one_time">One-Time / No Renewal</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Expiry Date */}
            <div>
              <Label>Plan Expiry Date (Optional - for test/trial)</Label>
              <Input 
                type="date"
                value={subForm.expires_at ? subForm.expires_at.split('T')[0] : ''}
                onChange={(e) => setSubForm({...subForm, expires_at: e.target.value || null})}
              />
              <p className="text-xs text-slate-500 mt-1">Leave empty for no expiry. Plan will auto-renew based on billing period.</p>
            </div>

            {/* When to Apply Changes */}
            <div className={`p-4 rounded-lg border ${isDark ? 'bg-slate-700/50 border-slate-600' : 'bg-amber-50 border-amber-200'}`}>
              <Label className={`mb-3 block font-semibold ${isDark ? 'text-white' : 'text-amber-800'}`}>
                ⚡ When to Apply Changes
              </Label>
              <div className="space-y-2">
                <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  subForm.apply_immediately ? 'border-amber-500 bg-amber-100' : isDark ? 'border-slate-600 bg-slate-700' : 'border-gray-200 bg-white'
                }`}>
                  <input
                    type="radio"
                    name="applyTiming"
                    checked={subForm.apply_immediately}
                    onChange={() => setSubForm({...subForm, apply_immediately: true, scheduled_date: ''})}
                    className="mt-1"
                  />
                  <div>
                    <p className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>Apply Immediately</p>
                    <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                      Changes take effect right now. Customer billing cycle continues with new settings.
                    </p>
                  </div>
                </label>
                
                <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  !subForm.apply_immediately && !subForm.scheduled_date ? 'border-amber-500 bg-amber-100' : isDark ? 'border-slate-600 bg-slate-700' : 'border-gray-200 bg-white'
                }`}>
                  <input
                    type="radio"
                    name="applyTiming"
                    checked={!subForm.apply_immediately && !subForm.scheduled_date}
                    onChange={() => setSubForm({...subForm, apply_immediately: false, scheduled_date: ''})}
                    className="mt-1"
                  />
                  <div>
                    <p className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>Apply at Term End</p>
                    <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                      Changes take effect when their current billing period ends 
                      {editingSubscription?.subscription?.expires_at && (
                        <span className="font-medium"> ({formatDate(editingSubscription.subscription.expires_at)})</span>
                      )}
                    </p>
                  </div>
                </label>
                
                <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  subForm.scheduled_date ? 'border-amber-500 bg-amber-100' : isDark ? 'border-slate-600 bg-slate-700' : 'border-gray-200 bg-white'
                }`}>
                  <input
                    type="radio"
                    name="applyTiming"
                    checked={!!subForm.scheduled_date}
                    onChange={() => setSubForm({...subForm, apply_immediately: false, scheduled_date: new Date().toISOString().split('T')[0]})}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <p className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>Schedule for Specific Date</p>
                    {subForm.scheduled_date && (
                      <Input 
                        type="date"
                        className="mt-2"
                        value={subForm.scheduled_date}
                        onChange={(e) => setSubForm({...subForm, scheduled_date: e.target.value})}
                        min={new Date().toISOString().split('T')[0]}
                      />
                    )}
                    <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                      Pick a future date for the changes to take effect.
                    </p>
                  </div>
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={saveSubscription} disabled={savingSub}>
              {savingSub ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Free Unlimited Confirmation Dialog */}
      <AlertDialog open={showFreeConfirm} onOpenChange={setShowFreeConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-violet-500" />
              Grant Free Unlimited Access
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                You are about to grant <strong className="text-violet-600">{freeConfirmCompany?.name}</strong> free unlimited access.
              </p>
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                <strong>This will:</strong>
                <ul className="list-disc ml-4 mt-1 space-y-1">
                  <li>Set monthly price to £0</li>
                  <li>Remove all seat, layout, and event limits</li>
                  <li>Mark the plan as a custom/unlimited plan</li>
                </ul>
              </div>
              <p className="text-sm">Are you sure you want to proceed?</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setShowFreeConfirm(false); setFreeConfirmCompany(null); }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={giveUnlimitedFree} className="bg-violet-600 hover:bg-violet-700">
              Yes, Grant Free Access
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Customer Details Dialog — pops when a company name is clicked.
          Includes Customer ID, owner email + phone, billing snapshot. */}
      <Dialog open={!!customerDetails} onOpenChange={(v) => { if (!v) setCustomerDetails(null); }}>
        <DialogContent className="max-w-lg" data-testid="customer-details-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-violet-500" />
              {customerDetails?.company?.name || 'Customer'}
            </DialogTitle>
            <DialogDescription>Customer profile snapshot from backoffice.</DialogDescription>
          </DialogHeader>
          {customerDetailsLoading ? (
            <div className="py-10 flex justify-center">
              <RefreshCw className="w-5 h-5 animate-spin text-violet-500" />
            </div>
          ) : (
            <div className="py-2 space-y-3 text-sm">
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-500">Customer ID</span>
                <code className="font-mono text-violet-600">{customerDetails?.company?.customer_id || `SP-${customerDetails?.company?.company_id?.slice(-6).toUpperCase()}`}</code>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-500">Internal Company ID</span>
                <code className="font-mono text-xs text-gray-500">{customerDetails?.company?.company_id}</code>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-500">Owner Email</span>
                <span className="font-medium">{customerDetails?.company?.owner_email || customerDetails?.profile?.owner_email || '—'}</span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-500">Phone</span>
                <span className="font-medium">{customerDetails?.profile?.phone || customerDetails?.profile?.contact_phone || customerDetails?.company?.phone || '—'}</span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-500">Website</span>
                <span className="font-medium">{customerDetails?.profile?.website || '—'}</span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-500">Address</span>
                <span className="font-medium text-right max-w-[60%]">{customerDetails?.profile?.address || '—'}</span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-500">Plan</span>
                <span className="font-medium">{customerDetails?.company?.subscription?.plan_name || customerDetails?.company?.subscription?.plan_id || '—'}</span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-500">Monthly price</span>
                <span className="font-medium">{formatCurrency(customerDetails?.company?.subscription?.monthly_price || 0)}</span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-500">Status</span>
                <span className="font-medium">{customerDetails?.company?.subscription?.status || 'none'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Renewal/Expiry</span>
                <span className={`font-medium ${customerDetails?.company?.subscription?.is_expired ? 'text-red-600' : (customerDetails?.company?.subscription?.expiring_soon ? 'text-amber-600' : '')}`}>
                  {customerDetails?.company?.subscription?.expires_at ? formatDate(customerDetails.company.subscription.expires_at) : 'No expiry'}
                </span>
              </div>
            </div>
          )}

          {/* Payment audit trail — full chargeback-defence log of every
              subscription/payment action with IP, timestamp, and actor. */}
          {customerDetails && !customerDetailsLoading && (
            <div className="mt-4 pt-4 border-t" data-testid="customer-audit-panel">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold">Payment audit log</h4>
                <span className="text-xs text-gray-400">Last {(customerDetails?.audit?.length || 0)} events</span>
              </div>
              {(!customerDetails?.audit || customerDetails.audit.length === 0) ? (
                <p className="text-xs text-gray-500 italic">No payment events recorded yet.</p>
              ) : (
                <div className="max-h-64 overflow-y-auto space-y-1 text-xs">
                  {customerDetails.audit.map((row) => (
                    <div key={row.audit_id} className="flex items-start gap-2 p-2 rounded border border-gray-100 bg-gray-50" data-testid={`audit-row-${row.audit_id}`}>
                      <span className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium ${row.action.includes('cancel') || row.action.includes('disabled') ? 'bg-red-100 text-red-700' : row.action.includes('enabled') || row.action.includes('created') ? 'bg-emerald-100 text-emerald-700' : 'bg-violet-100 text-violet-700'}`}>
                        {row.action}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{row.actor_email || row.actor_user_id || 'system'}</p>
                        <p className="text-[10px] text-gray-500">
                          {new Date(row.created_at).toLocaleString()} • IP {row.ip || '—'}{row.country ? ` (${row.country})` : ''}
                          {row.terms_accepted === true && <span className="ml-1 text-emerald-600 font-semibold">✓ T&amp;C accepted</span>}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCustomerDetails(null)}>Close</Button>
            {customerDetails?.company?.company_id && (
              <Button onClick={() => navigate(`/backoffice/customers/${customerDetails.company.company_id}`)}>
                Open full customer page
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Terminate Plan Dialog (super admin) — drives POST .../terminate-plan
          with either immediate or scheduled mode. Notifies the customer via
          the existing subscription_notifications channel. */}
      <Dialog open={showTerminateDialog} onOpenChange={setShowTerminateDialog}>
        <DialogContent className="max-w-lg" data-testid="terminate-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Ban className="w-5 h-5" />
              Terminate Subscription
            </DialogTitle>
            <DialogDescription>
              {terminateTarget?.name && (
                <span>This will end the plan for <strong>{terminateTarget.name}</strong>.</span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label>Termination mode</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => setTerminateForm(f => ({ ...f, mode: "immediate" }))}
                  data-testid="terminate-mode-immediate"
                  className={`px-3 py-2 rounded border text-sm font-medium transition-colors ${
                    terminateForm.mode === "immediate"
                      ? "bg-red-50 border-red-400 text-red-700"
                      : (isDark ? "border-slate-600 text-slate-300" : "border-gray-300 text-gray-700 hover:bg-gray-50")
                  }`}
                >
                  Immediate
                </button>
                <button
                  type="button"
                  onClick={() => setTerminateForm(f => ({ ...f, mode: "scheduled" }))}
                  data-testid="terminate-mode-scheduled"
                  className={`px-3 py-2 rounded border text-sm font-medium transition-colors ${
                    terminateForm.mode === "scheduled"
                      ? "bg-amber-50 border-amber-400 text-amber-700"
                      : (isDark ? "border-slate-600 text-slate-300" : "border-gray-300 text-gray-700 hover:bg-gray-50")
                  }`}
                >
                  On a date
                </button>
              </div>
            </div>

            {terminateForm.mode === "scheduled" && (
              <div>
                <Label htmlFor="terminate-at">Termination date</Label>
                <Input
                  id="terminate-at"
                  type="date"
                  value={terminateForm.terminate_at}
                  onChange={(e) => setTerminateForm(f => ({ ...f, terminate_at: e.target.value }))}
                  min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
                  data-testid="terminate-date-input"
                />
              </div>
            )}

            <div>
              <Label htmlFor="terminate-reason">Reason (visible to backoffice audit only)</Label>
              <Textarea
                id="terminate-reason"
                rows={3}
                placeholder="e.g. Repeated payment failure, customer request, terms violation"
                value={terminateForm.reason}
                onChange={(e) => setTerminateForm(f => ({ ...f, reason: e.target.value }))}
                data-testid="terminate-reason-input"
              />
            </div>

            <div className="flex items-center gap-2">
              <Switch
                id="terminate-notify"
                checked={terminateForm.notify_customer}
                onCheckedChange={(v) => setTerminateForm(f => ({ ...f, notify_customer: v }))}
                data-testid="terminate-notify-switch"
              />
              <Label htmlFor="terminate-notify" className="text-sm font-normal cursor-pointer">
                Notify customer via in-app notification
              </Label>
            </div>

            {terminateForm.mode === "immediate" && (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm flex gap-2">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <p>The customer will lose access to publish layouts and accept bookings <strong>immediately</strong>.</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowTerminateDialog(false)}
              disabled={terminating}
            >Cancel</Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={submitTerminate}
              disabled={terminating}
              data-testid="terminate-submit-btn"
            >
              {terminating ? "Terminating..." : (terminateForm.mode === "immediate" ? "Terminate now" : "Schedule termination")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Plan Dialog */}
      <Dialog open={showPlanDialog} onOpenChange={setShowPlanDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingPlan ? 'Edit Plan' : 'Create Plan'}</DialogTitle>
            <DialogDescription>Configure subscription plan details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Plan Name *</label>
              <Input 
                placeholder="e.g., Professional" 
                value={planForm.name} 
                onChange={(e) => setPlanForm({...planForm, name: e.target.value})} 
              />
            </div>

            {/* Pricing Section */}
            <div className={`p-3 rounded-lg border ${isDark ? 'bg-slate-700/50 border-slate-600' : 'bg-gray-50 border-gray-200'}`}>
              <h4 className={`text-sm font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Pricing</h4>
              <div className={`grid gap-3 ${isYearlyApplicable(planForm.name) ? 'grid-cols-2' : 'grid-cols-1'}`}>
                <div>
                  <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Monthly Price (£)</label>
                  <Input 
                    type="number" 
                    min="0"
                    step="0.01"
                    value={planForm.price_monthly} 
                    onChange={(e) => {
                      const monthly = parseFloat(e.target.value) || 0;
                      setPlanForm({...planForm, price_monthly: monthly, price_yearly: monthly * 12 * 0.8});
                    }} 
                  />
                </div>
                {isYearlyApplicable(planForm.name) && (
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Yearly Price (£) <span className="text-green-500">(20% off)</span></label>
                    <Input 
                      type="number" 
                      min="0"
                      step="0.01"
                      value={planForm.price_yearly} 
                      onChange={(e) => setPlanForm({...planForm, price_yearly: parseFloat(e.target.value) || 0})} 
                    />
                  </div>
                )}
              </div>
              {!isYearlyApplicable(planForm.name) && (
                <p className="text-xs text-amber-500 mt-2">💡 Yearly billing not available for free/trial/PAYG plans</p>
              )}
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Per Seat Price (£)</label>
                  <Input 
                    type="number" 
                    min="0"
                    step="0.01"
                    placeholder="e.g., 0.50"
                    value={planForm.per_seat_price} 
                    onChange={(e) => setPlanForm({...planForm, per_seat_price: parseFloat(e.target.value) || 0})} 
                  />
                  <p className="text-[10px] text-gray-400 mt-1">For PAYG plans</p>
                </div>
                <div>
                  <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Overage Per Seat (£)</label>
                  <Input 
                    type="number" 
                    min="0"
                    step="0.01"
                    placeholder="e.g., 0.30"
                    value={planForm.overage_per_seat} 
                    onChange={(e) => setPlanForm({...planForm, overage_per_seat: parseFloat(e.target.value) || 0})} 
                  />
                  <p className="text-[10px] text-gray-400 mt-1">After seat allowance</p>
                </div>
              </div>
              <div className="mt-3">
                <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Monthly Seat Allowance</label>
                <Input 
                  type="number" 
                  min="0"
                  placeholder="e.g., 10000 (0 for unlimited)"
                  value={planForm.monthly_seat_allowance} 
                  onChange={(e) => setPlanForm({...planForm, monthly_seat_allowance: parseInt(e.target.value) || 0})} 
                />
              </div>
            </div>

            {/* Limits Section */}
            <div className={`p-3 rounded-lg border ${isDark ? 'bg-slate-700/50 border-slate-600' : 'bg-gray-50 border-gray-200'}`}>
              <h4 className={`text-sm font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Limits</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Layouts</p>
                    <p className="text-xs text-gray-400">Max layouts per company</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {planForm.max_layouts === -1 ? (
                      <span className="text-green-500 text-sm font-medium">Unlimited</span>
                    ) : (
                      <Input 
                        type="number" 
                        min="1"
                        className="w-20"
                        value={planForm.max_layouts} 
                        onChange={(e) => setPlanForm({...planForm, max_layouts: parseInt(e.target.value) || 1})} 
                      />
                    )}
                    <button
                      type="button"
                      onClick={() => setPlanForm({...planForm, max_layouts: planForm.max_layouts === -1 ? 10 : -1})}
                      className={`text-xs px-2 py-1 rounded ${planForm.max_layouts === -1 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}
                    >
                      {planForm.max_layouts === -1 ? '✓ Unlimited' : 'Set Unlimited'}
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Events</p>
                    <p className="text-xs text-gray-400">Max events per company</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {planForm.max_events === -1 ? (
                      <span className="text-green-500 text-sm font-medium">Unlimited</span>
                    ) : (
                      <Input 
                        type="number" 
                        min="1"
                        className="w-20"
                        value={planForm.max_events} 
                        onChange={(e) => setPlanForm({...planForm, max_events: parseInt(e.target.value) || 1})} 
                      />
                    )}
                    <button
                      type="button"
                      onClick={() => setPlanForm({...planForm, max_events: planForm.max_events === -1 ? 50 : -1})}
                      className={`text-xs px-2 py-1 rounded ${planForm.max_events === -1 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}
                    >
                      {planForm.max_events === -1 ? '✓ Unlimited' : 'Set Unlimited'}
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Max Seats</p>
                    <p className="text-xs text-gray-400">Per layout capacity</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {planForm.max_seats === 0 ? (
                      <span className="text-green-500 text-sm font-medium">Unlimited</span>
                    ) : (
                      <Input 
                        type="number" 
                        min="1"
                        className="w-24"
                        value={planForm.max_seats} 
                        onChange={(e) => setPlanForm({...planForm, max_seats: parseInt(e.target.value) || 0})} 
                      />
                    )}
                    <button
                      type="button"
                      onClick={() => setPlanForm({...planForm, max_seats: planForm.max_seats === 0 ? 5000 : 0})}
                      className={`text-xs px-2 py-1 rounded ${planForm.max_seats === 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}
                    >
                      {planForm.max_seats === 0 ? '✓ Unlimited' : 'Set Unlimited'}
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>API Keys</p>
                    <p className="text-xs text-gray-400">Max API keys per company</p>
                  </div>
                  <Input 
                    type="number" 
                    min="1"
                    max="50"
                    className="w-20"
                    data-testid="plan-api-key-limit"
                    value={planForm.api_key_limit} 
                    onChange={(e) => setPlanForm({...planForm, api_key_limit: parseInt(e.target.value) || 1})} 
                  />
                </div>
              </div>
            </div>

            {/* Homepage Features Editor */}
            <div className={`p-3 rounded-lg border ${isDark ? 'bg-slate-700/50 border-slate-600' : 'bg-gray-50 border-gray-200'}`}>
              <h4 className={`text-sm font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Homepage Features</h4>
              <p className={`text-xs mb-3 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                Bullet points shown on the pricing card. Leave empty to auto-generate from plan settings.
              </p>
              <div className="space-y-2">
                {(planForm.homepage_features || []).map((feat, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Input
                      value={feat}
                      placeholder={`Feature ${idx + 1}`}
                      className="flex-1 h-8 text-sm"
                      onChange={(e) => {
                        const updated = [...planForm.homepage_features];
                        updated[idx] = e.target.value;
                        setPlanForm({...planForm, homepage_features: updated});
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const updated = planForm.homepage_features.filter((_, i) => i !== idx);
                        setPlanForm({...planForm, homepage_features: updated});
                      }}
                      className="text-red-400 hover:text-red-600 p-1"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full mt-1"
                  onClick={() => setPlanForm({...planForm, homepage_features: [...(planForm.homepage_features || []), ""]})}
                >
                  <Plus className="w-3 h-3 mr-1" /> Add Feature
                </Button>
              </div>
            </div>

            {/* Settings */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  checked={planForm.is_active}
                  onChange={(e) => setPlanForm({...planForm, is_active: e.target.checked})}
                  className="w-4 h-4 rounded"
                />
                <span className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Plan is active</span>
              </div>
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  checked={planForm.show_on_homepage}
                  onChange={(e) => setPlanForm({...planForm, show_on_homepage: e.target.checked})}
                  className="w-4 h-4 rounded"
                />
                <span className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Show on homepage pricing section</span>
              </div>
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  checked={planForm.is_popular}
                  onChange={(e) => setPlanForm({...planForm, is_popular: e.target.checked})}
                  className="w-4 h-4 rounded"
                />
                <span className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Mark as &quot;Most Popular&quot;</span>
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Description</label>
              <Input 
                placeholder="Brief description of this plan" 
                value={planForm.description} 
                onChange={(e) => setPlanForm({...planForm, description: e.target.value})} 
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowPlanDialog(false)}>Cancel</Button>
              <Button onClick={savePlan}>{editingPlan ? 'Update' : 'Create'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
